import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { ExperiencePersonaDirective } from '@/components/admin/experiences/experience-persona-directive';
import { ExperiencePersonaOverlay } from '@/components/admin/experiences/experience-persona-overlay';
import { ExperiencePersonaPicker } from '@/components/admin/experiences/experience-persona-picker';
import {
  useVariableScope,
  useVariableSources,
} from '@/components/admin/experiences/experience-variable-scope';
import { PersonaCardGauge, PersonaCardIdentity } from '@/components/admin/personas/persona-card';
import { ReferenceControl } from '@/components/admin/versioned/reference-control';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import { Button, buttonVariants } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { useAdminSession } from '@/lib/admin-session';
import {
  fetchInvolvementLevels,
  fetchPersona,
  fetchPersonaVersion,
  fetchPersonas,
} from '@/lib/api';
import type { ExperienceDraft } from '@/lib/experience-draft';
import { withPersonaOverlay, withPersonaRef } from '@/lib/experience-draft';
import type { PaletteContent } from '@/lib/variable-palette';
import { variablePalette } from '@/lib/variable-palette';
import { variableZoneOf } from '@/lib/variable-pill';
import type { PersonaContent } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * L'onglet Persona de l'éditeur d'expérience (refonte modulaire §10.2 ;
 * maquettes `OngletPersona` et `OngletPersonaVide`).
 *
 * §10.2 says it does three things and nothing else: it CHOOSES an existing
 * persona, it carries the OVERLAY, and it shows in read-only « Ce que
 * l'interviewer recevra ». The third is `experience-persona-directive.tsx`, the
 * second `experience-persona-overlay.tsx`, and what is left here is the choice
 * and the two states the mockup draws around it.
 *
 * ## No editing of the persona on the spot, and that is a rule and not an omission
 *
 * §10.2: « une seule orthographe du persona, un seul endroit où la corriger, et
 * une version de persona créée "en passant" depuis une autre page se
 * retrouverait dans "utilisé dans" de trois expériences sans que personne l'ait
 * voulu ». So the card of the chosen persona is READ - the choice passes by
 * « Remplacer le persona » and by the pill-menu of the version played, never by
 * a click on the card - and the way out of this screen is « Ouvrir dans
 * Personas », a link.
 *
 * ## The empty state is a state of the SPEC, not a loading state
 *
 * `OngletPersonaVide`: the dashed card, the selector already open underneath,
 * the overlay inactive, the read-only panel saying « Rien
 * pour l'instant », and a yellow callout recalling that the version will not
 * save while a protocol is chosen without a persona (§9.1). Every one of those
 * is drawn from `draft.persona === null` and from nothing else: a library still
 * in flight shows the same card with its selector empty, which is honest, and
 * the callout is about the CONTENT rather than about the network.
 *
 * ## The resolved persona is read at the experience's PIN
 *
 * The directive is « rendu depuis la version résolue » (§10.2, vitrine.md §3.1),
 * so a reference pinned to a precise version must render THAT version and not
 * the development one the detail carries. That is a second read, scoped to the
 * version, and it is `enabled` only when the pin asks for it - an experience at
 * `latest`, which is most of them while they are written, pays one read.
 *
 * Both keys are the ones `/admin/personas` already writes (`['persona', email,
 * id]`, `['persona-version', email, id, versionId]`), deliberately: same
 * fetcher, same schema, same shape, so a persona edited in its own library
 * shows up here without a reload. The trap of issue #16 is two readers agreeing
 * on a key and disagreeing on what is under it, which is the opposite of this.
 */

export interface ExperiencePersonaTabProps {
  draft: ExperienceDraft;
  /** A version opened by « Voir », or a retired experience: nothing is typable. */
  readOnly: boolean;
  onDraft: (next: ExperienceDraft) => void;
}

export function ExperiencePersonaTab({ draft, readOnly, onDraft }: ExperiencePersonaTabProps) {
  const { email } = useAdminSession();
  const [picking, setPicking] = useState(false);

  const chosen = draft.persona;
  const personaId = chosen?.ref.id ?? null;
  // `latest` until the operator pins a version: the default of every reference.
  const pin = chosen?.ref.pin ?? 'latest';

  const library = useQuery({
    queryKey: ['personas', email],
    queryFn: fetchPersonas,
    retry: false,
  });

  const scale = useQuery({
    queryKey: ['involvement-levels', email],
    queryFn: fetchInvolvementLevels,
    retry: false,
  });

  const detail = useQuery({
    queryKey: ['persona', email, personaId],
    queryFn: () => fetchPersona(personaId ?? ''),
    enabled: personaId !== null,
    retry: false,
  });

  // The version a NUMBER names, when it is not the development one the detail
  // already carries; null for `latest` and for a number the persona never had.
  const pinned =
    detail.data === undefined || pin === 'latest' || pin === detail.data.development.number
      ? null
      : (detail.data.versions.find((version) => version.number === pin) ?? null);
  const pinnedId = pinned?.id ?? null;
  const pinnedRead = useQuery({
    queryKey: ['persona-version', email, personaId, pinnedId],
    queryFn: () => fetchPersonaVersion(personaId ?? '', pinnedId ?? ''),
    enabled: personaId !== null && pinnedId !== null,
    retry: false,
  });

  const personas = library.data?.personas ?? [];
  const levels = scale.data?.levels ?? [];
  const card = personas.find((one) => one.id === personaId) ?? null;

  /*
    The content the directive renders: the pinned version when the reference
    asks for one, the development content - « latest dev » - otherwise. NULL
    while the read is in flight, and the panel then says « Rien pour
    l'instant » - which is what it would say for a persona that has no such
    version, and that is the same fact: the strict resolution of §4 does not
    fall back.
  */
  const resolved: PersonaContent | null =
    pin === 'latest' || (detail.data !== undefined && pin === detail.data.development.number)
      ? (detail.data?.developmentContent ?? null)
      : (pinnedRead.data?.content ?? null);

  /*
    Les deux zones de variables de cet onglet (§8.6 ; grill Variables D3, D7) :
    la surcouche et le Contexte sont servis pendant que le protocole se joue,
    donc les deux voient les champs et les choix des pages d'avant la page
    protocole, les objectifs et les critères - deux places pour la règle de
    portée, un seul lecteur des noms, la portée que l'éditeur monte au-dessus.
  */
  const sources = useVariableSources(draft);
  const { experienceId } = useVariableScope();
  const overlayZone = useMemo(
    () => variableZoneOf(sources, { kind: 'overlay' }, experienceId),
    [sources, experienceId],
  );
  const contextZone = useMemo(
    () => variableZoneOf(sources, { kind: 'context' }, experienceId),
    [sources, experienceId],
  );
  const overlayPalette = (query: string): PaletteContent =>
    variablePalette(sources, { kind: 'overlay' }, query);

  const showPicker = !readOnly && (picking || chosen === null);

  return (
    <section data-testid={EXPERIENCE_IDS.persona} className="space-y-5">
      <div className="space-y-1">
        <h2 className="font-semibold text-bleu-nuit text-lg leading-7">Persona</h2>
        <p className="max-w-[95ch] text-gray-600 text-sm">
          Qui conduit l’entretien sur la page protocole. Le persona se corrige dans Personas ; ici,
          on le choisit et on prolonge sa direction pour cette expérience.
        </p>
      </div>

      {chosen === null ? (
        <div
          data-testid={EXPERIENCE_IDS.personaEmpty}
          className="flex flex-col items-start gap-2 rounded-md border-2 border-gray-300 border-dashed bg-white p-5"
        >
          <span className="inline-flex items-center gap-2 font-semibold text-bleu-nuit text-lg leading-7">
            <UserPlus aria-hidden="true" className="size-5" />
            Aucun persona choisi
          </span>
          <p className="text-gray-600 text-sm">
            La page protocole ne peut pas se jouer sans quelqu’un pour conduire l’entretien.
          </p>
          {readOnly ? null : (
            <Button
              type="button"
              data-testid={EXPERIENCE_IDS.personaChoose}
              size="sm"
              onClick={() => setPicking(true)}
            >
              Choisir un persona
            </Button>
          )}
        </div>
      ) : (
        <div
          data-testid={EXPERIENCE_IDS.personaCard}
          className="flex flex-wrap items-start justify-between gap-4 rounded-md border-2 border-gray-200 bg-white p-5"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            {card === null ? (
              <p className="text-gray-700 text-sm">
                Ce persona n’est plus dans la bibliothèque. Remplacez-le, ou restaurez-le dans
                Personas.
              </p>
            ) : (
              <>
                <PersonaCardIdentity card={card} />
                <PersonaCardGauge card={card} levels={levels} />
                <div className="flex flex-wrap gap-2">
                  {card.production === null ? null : (
                    <VersionPill
                      entity="persona"
                      tag="production"
                      version={card.production.number}
                    />
                  )}
                  <VersionPill
                    entity="persona"
                    tag="development"
                    version={card.development.number}
                  />
                </div>
                {card.retiredAt === null ? null : (
                  <p className="text-jaune-deep text-xs leading-4">
                    Ce persona est retiré. Il reste joué tant qu’il n’est pas remplacé.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2">
            {card === null ? null : (
              <div className="flex max-w-full flex-col items-start gap-1">
                <span className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
                  Version jouée
                </span>
                <ReferenceControl
                  subject={{
                    kind: 'persona',
                    id: chosen.ref.id,
                    name: card.name,
                    who: 'cette expérience',
                    pin,
                    developmentNumber: card.development.number,
                  }}
                  readOnly={readOnly}
                  testId={EXPERIENCE_IDS.personaPin}
                  onPin={(next) => onDraft(withPersonaRef(draft, { id: chosen.ref.id, pin: next }))}
                />
              </div>
            )}
            {readOnly ? null : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid={EXPERIENCE_IDS.personaChoose}
                onClick={() => setPicking((was) => !was)}
              >
                Remplacer le persona
              </Button>
            )}
            <Link
              to="/admin/personas/$personaId"
              params={{ personaId: chosen.ref.id }}
              data-testid={EXPERIENCE_IDS.personaOpen}
              className={`${buttonVariants({ variant: 'ghost', size: 'sm' })} no-underline`}
            >
              Ouvrir dans Personas
            </Link>
          </div>
        </div>
      )}

      {showPicker ? (
        <ExperiencePersonaPicker
          personas={personas}
          levels={levels}
          onChoose={(id) => {
            // A new choice is `latest`: épingler est l'exception (V16).
            onDraft(withPersonaRef(draft, { id, pin: 'latest' }));
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}

      {chosen === null ? (
        <Callout tone="warn">
          Tant qu’un protocole est choisi sur la page protocole, la version ne s’enregistre pas sans
          persona (§9.1). Une expérience sans protocole s’enregistre sans persona.
        </Callout>
      ) : null}

      <ExperiencePersonaOverlay
        value={chosen?.overlay ?? ''}
        readOnly={readOnly || chosen === null}
        zone={overlayZone}
        palette={overlayPalette}
        onChange={(next) => onDraft(withPersonaOverlay(draft, next))}
      />

      <ExperiencePersonaDirective
        persona={resolved}
        overlay={chosen?.overlay ?? ''}
        context={draft.context}
        overlayZone={overlayZone}
        contextZone={contextZone}
      />
    </section>
  );
}
